(() => {
  const groups = {
    atv_tire: ['atv-utv-wheels-tires'], atv_wheel: ['atv-utv-wheels'],
    vehicle_tire: ['shop-vehicle-tires'], vehicle_wheel: ['aftermarket-wheels-1', 'oe-wheels-1'],
    trailer_tire: ['trailer-tires'], trailer_wheel: ['trailer-wheels']
  };
  const normalize = value => String(value || '').toUpperCase().replace(/\s+/g, '').replace(/×/g, 'X');
  const tags = product => Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map(s => s.trim());
  const tag = (product, key) => tags(product).find(t => t.toLowerCase().startsWith(key.toLowerCase() + ':'))?.split(':').slice(1).join(':').trim() || '';
  function category(product) {
    const type = String(product.product_type || product.type || '').toLowerCase();
    if (/motorcycle|agricultural|assembl/.test(type)) return '';
    const kind = /wheel/.test(type) ? 'wheel' : /tire/.test(type) ? 'tire' : '';
    if (!kind) return '';
    if (/atv|utv/.test(type)) return 'atv_' + kind;
    if (/trailer/.test(type)) return 'trailer_' + kind;
    if (/passenger|light truck|vehicle|^tires?$|^wheels?$|aftermarket|\boe\b/.test(type)) return 'vehicle_' + kind;
    return '';
  }
  function size(product, explicit) {
    const supplied = explicit || tag(product, 'Size') || tag(product, 'Tire-Size');
    const text = normalize(supplied || product.title);
    const metric = text.match(/(?:LT|ST|P)?\d{3}\/\d{2}(?:ZR|R|D|B|-)\d{2}(?:\.5)?/);
    if (metric) return metric[0];
    const flotation = text.match(/\d{2}(?:\.\d+)?X\d{1,2}(?:\.\d+)?(?:R|-)\d{2}(?:\.\d+)?/);
    return flotation ? flotation[0].replace('R', '-') : '';
  }
  const wheelSpec = product => ['Wheel-Diameter', 'Wheel-Width', 'Bolt-Pattern', 'Offset-Configuration'].map(k => normalize(tag(product, k)));
  function eligible(source, candidate, explicit) {
    const group = category(source);
    if (!group || category(candidate) !== group || candidate.id === source.id || !candidate.published_at) return false;
    if (!candidate.variants?.some(v => v.available && Number(v.price) > 0)) return false;
    if (group.endsWith('_tire')) {
      const reference = size(source, explicit);
      return !!reference && reference === size(candidate);
    }
    const a = wheelSpec(source), b = wheelSpec(candidate);
    return a.every((value, i) => !!value && value === b[i]);
  }
  const cache = new Map();
  async function page(root, handle, number) {
    const url = `${root}collections/${handle}/products.json?limit=250&page=${number}&sort_by=created-descending`;
    if (!cache.has(url)) cache.set(url, fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => {
      if (!r.ok) throw new Error('Recommendations unavailable');
      return r.json();
    }).then(data => data.products || []).catch(error => { cache.delete(url); throw error; }));
    return cache.get(url);
  }
  function card(product, currency, labels, root) {
    const item = document.createElement('li');
    item.className = 'grid__item';
    item.dataset.productId = product.id;
    const wrap = document.createElement('div');
    wrap.className = 'card-wrapper product-card-wrapper';
    const link = document.createElement('a');
    link.className = 'apex-setup__link';
    link.href = `${root}products/${encodeURIComponent(product.handle)}`;
    const photo = product.images?.[0];
    if (photo?.src) {
      const img = document.createElement('img');
      const url = new URL(photo.src, document.baseURI);
      url.searchParams.set('width', '600');
      img.src = url.href;
      img.alt = photo.alt || product.title;
      img.width = 600; img.height = 600; img.loading = 'lazy'; img.decoding = 'async';
      img.className = 'apex-setup__image';
      link.append(img);
    }
    const info = document.createElement('div');
    info.className = 'apex-setup__info';
    const title = document.createElement('h3');
    title.className = 'card__heading h5'; title.textContent = product.title;
    const price = document.createElement('div');
    price.className = 'price';
    const prices = product.variants.filter(v => v.available && Number(v.price) > 0).map(v => Number(v.price));
    price.textContent = (new Set(prices).size > 1 ? labels.from + ' ' : '') + new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(Math.min(...prices));
    info.append(title, price); link.append(info); wrap.append(link); item.append(wrap);
    return item;
  }
  class ApexSetup extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); this.load(); }
      }, { rootMargin: '600px' });
      this.hidden = false;
      this.style.minHeight = '1px';
      this.querySelector('h2').hidden = true;
      this.querySelector('p').hidden = true;
      observer.observe(this);
    }
    async load() {
      const data = JSON.parse(this.querySelector('[data-apex-source]').textContent);
      const labels = JSON.parse(this.querySelector('[data-apex-labels]').textContent);
      const group = category(data.product);
      if (!groups[group]) { this.hidden = true; return; }
      const found = [], seen = new Set();
      const root = this.dataset.root.endsWith('/') ? this.dataset.root : this.dataset.root + '/';
      const grid = this.querySelector('ul');
      try {
        for (const handle of groups[group]) {
          for (let number = 1; number <= 100 && found.length < 4; number++) {
            const products = await page(root, handle, number);
            for (const product of products) {
              if (!seen.has(product.id) && eligible(data.product, product, data.size)) {
                seen.add(product.id); found.push(product);
                grid.append(card(product, data.currency || 'USD', labels, root));
                this.querySelector('h2').hidden = false;
                this.querySelector('p').textContent = labels[group.endsWith('_tire') ? 'tires' : 'wheels'];
                this.querySelector('p').hidden = false;
                if (found.length === 4) break;
              }
            }
            if (products.length < 250) break;
          }
        }
      } catch (_) { }
      this.hidden = found.length === 0;
      this.style.minHeight = '';
    }
  }
  if (!customElements.get('apex-setup')) customElements.define('apex-setup', ApexSetup);
  window.ApexSetupRules = Object.freeze({ category, size, eligible, wheelSpec });
})();
